import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { DistributionStatusEnum, DistributionTypeEnum } from '../enums/distribution-operations.enums';
import { DistributionReportTotalStreams, DistributionReportTrackStats, DistributionResponse, ReportReviewSummary, ImportedReport, UpdateDistributionDto, DistributionIncomeStream, OnAccountDistributionDto, GetLabelsAndCollectorsByDistributionIdResponse, DistributionStatusCountResponse, ChangePositionItem, UpsertInvoiceNumberResponse, ResidualDistributionItem, CreateResidualDistributionsResponse, ResidualDistributionJobStatus } from '../models/distribution.model';
import { PublishAcceptedResponse, PublishStatusResponse } from '../models/publish-job.model';
import { Report } from 'src/app/search-assign/shared/models/interfaces.model';


@Injectable({
  providedIn: 'root'
})
export class DistributionService {

  protected newDistributionServiceUrl = AppConfig.settings.apiServer.newDistributionServiceUrl;
  protected searchAssignServiceUrl = AppConfig.settings.apiServer.searchAssignServiceUrl;
  protected distributionUri = `${this.newDistributionServiceUrl}/Distribution`;
  protected exportDistributionUri = `${this.newDistributionServiceUrl}/Export`;
  protected onAccountDistributionUri = `${this.newDistributionServiceUrl}/DistributionOnAccount`;
  protected financialGroupUri = `${this.newDistributionServiceUrl}/FinancialGroup`;
  protected residualDistributionUri = `${this.newDistributionServiceUrl}/ResidualDistributions`;
  constructor(private http: HttpClient) { }


  GetDistributionByStatus(
    status: DistributionStatusEnum,
    pageIndex: number = 0,
    pageSize: number = 10,
    keyword?: string
  ): Observable<DistributionResponse> {
    let params = new HttpParams()
      .set('status', status.toString())
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (keyword && keyword.trim()) {
      params = params.set('keyword', keyword.trim());
    }

    return this.http.get<DistributionResponse>(
      `${this.distributionUri}/GetDistributionByStatus`,
      { params }
    );
  }

  GetResidualDistributions(): Observable<ResidualDistributionItem[]> {
    return this.http.get<ResidualDistributionItem[]>(
      `${this.residualDistributionUri}/GetResduialDistributions`
    );
  }

  ExportResidualDistributionItems(distributionId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('distributionId', distributionId.toString());

    return this.http.get(
      `${this.residualDistributionUri}/exportResdiualDistributionItems`,
      { params, responseType: 'blob' }
    );
  }

  CreateResidualDistributions(distributionIds: number[]): Observable<CreateResidualDistributionsResponse> {
    return this.http.post<CreateResidualDistributionsResponse>(
      `${this.residualDistributionUri}/CreateResidualDistributions`,
      distributionIds
    );
  }

  GetResidualDistributionJobStatus(jobId: string): Observable<ResidualDistributionJobStatus> {
    const params = new HttpParams()
      .set('jobId', jobId);

    return this.http.get<ResidualDistributionJobStatus>(
      `${this.residualDistributionUri}/GetResidualDistributionJobStatus`,
      { params }
    );
  }

  GetDistributionById(distributionId: number): Observable<any> {
    let params = new HttpParams()
      .set('distributionId', distributionId.toString());
    return this.http.get<any>(
      `${this.distributionUri}/GetDistributionById`,
      { params }
    );
  }

  GetDistributionsReports(organizationId: number): Observable<Report[]> {
    let params = new HttpParams()
      .set('organizationId', organizationId.toString());

    return this.http.get<Report[]>(
      `${this.searchAssignServiceUrl}/Report/list-distribution-reports`,
      { params }
    );
  }


  GetReportTrackStats(reportId: number, organizationId: number): Observable<DistributionReportTrackStats> {
    let params = new HttpParams()
      .set('organizationId', organizationId.toString());

    return this.http.get<DistributionReportTrackStats>(
      `${this.searchAssignServiceUrl}/Report/tracks-stats/${reportId}`,
      { params }
    );
  }

  CreateDistribution(organizationId: number, distributionTypeId: number, distributionStatusId: number): Observable<{ distributionId: number }> {
    const body = {
      organizationId,
      distributionTypeId,
      distributionStatusId
    };

    return this.http.post<{ distributionId: number }>(
      `${this.distributionUri}/CreateDistribution`,
      body
    );
  }

  UpdateDistribution(updateDistributionDto: UpdateDistributionDto): Observable<void> {
    return this.http.put<void>(
      `${this.distributionUri}/UpdateDistribution`,
      updateDistributionDto
    );
  }

  GetDistributionReportTotalStreams(reportId: number, itemIds: number[]): Observable<DistributionReportTotalStreams> {
    return this.http.post<DistributionReportTotalStreams>(
      `${this.distributionUri}/GetDistributionReportTotalStreams/${reportId}`,
      itemIds
    );
  }

  GetDistributionReportReview(
    distributionId: number,
    reportId: number,
    itemIds: number[],
    splitPercentage: number,
    totalReceivedFundsExcludedVat: number,
    calculationMethod: string
  ): Observable<ReportReviewSummary> {

    const body = {
      distributionId,
      reportId,
      itemIds,
      splitPercentage,
      totalReceivedFundsExcludedVat,
      calculationMethod
    };

    return this.http.post<ReportReviewSummary>(
      `${this.distributionUri}/DistributionReportReview`,
      body
    );
  }

  SaveDistributionReport(report: ImportedReport): Observable<void> {
    const body = {
      ...report,
    };

    return this.http.post<void>(
      `${this.distributionUri}/InsertDistributionReport`,
      body
    );
  }

  DeleteDistributionReport(distributionReportId: number): Observable<void> {
    const params = new HttpParams()
      .set('distributionReportId', distributionReportId.toString());

    return this.http.delete<void>(
      `${this.distributionUri}/DeleteDistributionReport`,
      { params }
    );
  }

  InsertDistributionIncomeStreams(distributionId: number, incomeStreams: DistributionIncomeStream[]): Observable<void> {
    const body = {
      distributionId,
      incomeStreams
    };

    return this.http.post<void>(
      `${this.distributionUri}/InsertDistributionIncomeStreams`,
      body
    );
  }

  DeleteDistribution(distributionId: number): Observable<void> {
    const params = new HttpParams()
      .set('distributionId', distributionId.toString());
    return this.http.delete<void>(
      `${this.distributionUri}/DeleteDistribution`,
      { params }
    );
  }

  // ON ACCOUNT DISTRIBUTION METHODS
  CreateOnAccountDistribution(onAccountDistributionDto: OnAccountDistributionDto): Observable<any> {
    const body = {
      ...onAccountDistributionDto,
    };

    return this.http.post<any>(
      `${this.onAccountDistributionUri}/CreateDistributionOnAccount`,
      body
    );
  }

  DeleteOnAccountDistribution(distributionOnAccountId: number): Observable<void> {

    return this.http.delete<void>(
      `${this.onAccountDistributionUri}/DeleteDistributionOnAccount/${distributionOnAccountId}`,
    );
  }

  GetNonDeductedDistributionsOnAccount(): Observable<any> {
    return this.http.get<any>(
      `${this.onAccountDistributionUri}/GetNonDeductedDistributionsOnAccount`
    );
  }

  SaveDeductedOnAccountDistribution(distributionId: number, distributionOnAccountId: number): Observable<void> {
    return this.http.patch<void>(
      `${this.onAccountDistributionUri}/SavedDeductedOnAccountDistribution/${distributionId}/${distributionOnAccountId}`,
      {}
    );
  }

  // FINANCIAL GROUP METHODS
  GetFinancialGroupsByOrganizationId(organizationId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.financialGroupUri}/ByOrganization/${organizationId}`
    );
  }

  InsertDistributionFinancialGroup(distributionId: number, targetDistributionId: number): Observable<any> {
    const body = {
      distributionId,
      targetDistributionId
    };

    return this.http.post<any>(
      `${this.financialGroupUri}/InsertDistributionFinancialGroup`,
      body
    );
  }

  GetLabelsAndCollectorsByDistributionId(distributionId: number): Observable<GetLabelsAndCollectorsByDistributionIdResponse> {
    return this.http.get<GetLabelsAndCollectorsByDistributionIdResponse>(
      `${this.distributionUri}/GetLabelsAndCollectorsByDistributionId?distributionId=${distributionId}`
    );
  }

  MoveToReadyForProcessing(distributionIds: number[]): Observable<any> {
    return this.http.post<any>(
      `${this.distributionUri}/MoveToReadyForProcessing`,
      distributionIds
    );
  }

  MoveToDraft(distributionIds: number[]): Observable<any> {
    return this.http.post<any>(
      `${this.distributionUri}/MoveToDraft`,
      distributionIds
    );
  }

  ChangePosition(items: ChangePositionItem[]): Observable<any> {
    return this.http.post<any>(
      `${this.distributionUri}/ChangePosition`,
      items
    );
  }

  SendToAccounting(distributionIds: number[]): Observable<any> {
    return this.http.post<any>(
      `${this.distributionUri}/SendToAccounting`,
      distributionIds
    );
  }

  PublishDistributions(distributionIds: number[]): Observable<PublishAcceptedResponse> {
    return this.http.post<PublishAcceptedResponse>(
      `${this.distributionUri}/PublishDistributions`,
      distributionIds
    );
  }

  PublishAndSendDistributionNotificationEmails(distributionIds: number[]): Observable<PublishAcceptedResponse> {
    return this.http.post<PublishAcceptedResponse>(
      `${this.distributionUri}/PublishAndSendDistributionNotificationEmails`,
      distributionIds
    );
  }

  GetPublishStatus(jobId: string): Observable<PublishStatusResponse> {
    return this.http.get<PublishStatusResponse>(
      `${this.distributionUri}/PublishStatus/${jobId}`
    );
  }

  // Pass null to fetch the currently saved invoice number without changing it
  UpsertInvoiceNumber(invoiceNumber: number | null): Observable<UpsertInvoiceNumberResponse> {
    return this.http.post<UpsertInvoiceNumberResponse>(
      `${this.distributionUri}/UpsertInvoiceNumber`,
      invoiceNumber
    );
  }

  ExportLiveDbDistribution(distributionId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('distributionId', distributionId.toString());

    return this.http.get(
      `${this.exportDistributionUri}/ExportLiveDbDistribution`,
      { params, responseType: 'blob' }
    );
  }

  ExportAllDistributionTracks(distributionId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('distributionId', distributionId.toString());

    return this.http.get(
      `${this.distributionUri}/ExportAllDistributionTracks`,
      { params, responseType: 'blob' }
    );
  }


  ExportPublishedDistributionsFromBlob(distributionId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('distributionId', distributionId.toString());

    return this.http.get(
      `${this.exportDistributionUri}/ExportPublishedDistributionsFromBlob`,
      { params, responseType: 'blob' }
    );
  }



  GetReadyFinancialData(): Observable<Blob> {
    return this.http.get(
      `${this.exportDistributionUri}/GetReadyFinancialData`,
      { responseType: 'blob' }
    );
  }

}
